import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Avatar,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
    alpha
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Group as GroupIcon,
    Event as EventIcon,
    CalendarToday as CalendarIcon,
    Email as EmailIcon,
    ExitToApp as SignOutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const navigationItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { label: 'Groups', path: '/groups', icon: <GroupIcon /> },
    { label: 'Parties', path: '/parties', icon: <EventIcon /> },
    { label: 'Calendar', path: '/calendar', icon: <CalendarIcon /> },
    { label: 'Invitations', path: '/invitations', icon: <EmailIcon /> }
];

export default function Sidebar() {
    const { currentUser, signOutUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const handleNavigation = (path) => {
        navigate(path);
    };

    const handleSignOut = async () => {
        try {
            await signOutUser();
            navigate('/signin');
        } catch (error) {
            console.error('Sign out error:', error);
        }
    };

    return (
        <Box
            sx={{
                width: 280,
                height: '96vh',
                position: 'fixed',
                left: 16,
                top: '2vh',
                borderRadius: 4,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha('#ffffff', 0.08)}`,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 1200,
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
            }}
        >
            {/* Logo Area */}
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 800,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.5px'
                    }}
                >
                    Party Planner
                </Typography>
            </Box>

            {/* Navigation Items */}
            <List sx={{ px: 2, flexGrow: 1 }}>
                {navigationItems.map((item) => {
                    const isActive = location.pathname === item.path || (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

                    return (
                        <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                sx={{
                                    borderRadius: 3,
                                    py: 1.5,
                                    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
                                    color: isActive ? 'primary.main' : 'text.secondary',
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        transform: 'translateX(4px)',
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 40,
                                        color: isActive ? 'primary.main' : 'inherit',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 600 : 500,
                                        fontSize: '0.95rem'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>

            {/* User Profile Area */}
            <Box sx={{ p: 2, mt: 'auto' }}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: alpha('#000', 0.2),
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Avatar
                        src={currentUser?.photoURL}
                        alt={currentUser?.displayName}
                        sx={{ width: 40, height: 40, border: `2px solid ${theme.palette.primary.main}` }}
                    />
                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                            {currentUser?.displayName || 'User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {currentUser?.email}
                        </Typography>
                    </Box>
                    <Tooltip title="Sign Out">
                        <IconButton size="small" onClick={handleSignOut} sx={{ color: 'error.main' }}>
                            <SignOutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
        </Box>
    );
}
